import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AiOutlineAudio, AiOutlineAudioMuted } from "react-icons/ai";
import { LuMessageCircle, LuMinus, LuVolume2, LuVolumeX } from "react-icons/lu";
import { streamBlogWriter, warmBlogWriter } from "../../utils/agentsApi";
import { talkBlogWriter } from "../../utils/apiUtils";
import { hasBlogBody } from "./blogShared.jsx";
import { readComposeDraft, writeComposeDraft } from "./blogComposeDraft.js";

const OPENER = "Hey — I'll help you write this. What's the post about, in a sentence?";
const WORKING = "Working on it…";
const PCM_IN = 16000;
const PCM_OUT = 24000;
const MIC_SHORTCUT = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)
    ? "⌥M"
    : "Alt+M";

const isMicShortcut = (event) => {
    const isM = event.code === "KeyM" || event.key === "m" || event.key === "M";
    return isM && event.altKey && !event.metaKey && !event.ctrlKey;
};

const speakReply = (text, { onend } = {}) => {
    if (!text || typeof window === "undefined" || !window.speechSynthesis) {
        onend?.();
        return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    utterance.rate = 1.02;
    const voices = window.speechSynthesis.getVoices();
    const voice = voices.find((item) => /en-US/i.test(item.lang) && /natural|samantha|jenny|aria|google/i.test(item.name))
        || voices.find((item) => /en-US/i.test(item.lang));
    if (voice) utterance.voice = voice;
    utterance.onend = () => onend?.();
    utterance.onerror = () => onend?.();
    window.speechSynthesis.speak(utterance);
};

const floatToPcm16 = (samples) => {
    const pcm = new Int16Array(samples.length);
    for (let i = 0; i < samples.length; i += 1) {
        const sample = Math.max(-1, Math.min(1, samples[i]));
        pcm[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }
    return pcm;
};

const downsample = (input, inRate, outRate) => {
    if (inRate === outRate) return input;
    const ratio = inRate / outRate;
    const out = new Float32Array(Math.round(input.length / ratio));
    for (let i = 0; i < out.length; i += 1) {
        const index = i * ratio;
        const lo = Math.floor(index);
        const hi = Math.min(lo + 1, input.length - 1);
        const frac = index - lo;
        out[i] = input[lo] * (1 - frac) + input[hi] * frac;
    }
    return out;
};

const pcm16ToBase64 = (pcm) => {
    const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
    let binary = "";
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(binary);
};

const decodePcm16 = (base64) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const view = new DataView(bytes.buffer);
    const floats = new Float32Array(Math.floor(bytes.length / 2));
    for (let i = 0; i < floats.length; i += 1) {
        floats[i] = view.getInt16(i * 2, true) / 0x8000;
    }
    return floats;
};

const startPcmCapture = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
    });
    const ctx = new AudioContext();
    const source = ctx.createMediaStreamSource(stream);
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    const chunks = [];
    processor.onaudioprocess = (event) => {
        chunks.push(new Float32Array(event.inputBuffer.getChannelData(0)));
    };
    const mute = ctx.createGain();
    mute.gain.value = 0;
    source.connect(processor);
    processor.connect(mute);
    mute.connect(ctx.destination);
    return { stream, ctx, source, processor, mute, chunks };
};

const stopPcmCapture = async (rec) => {
    if (!rec) return "";
    try { rec.processor.disconnect(); } catch { /* already stopped */ }
    try { rec.source.disconnect(); } catch { /* already stopped */ }
    try { rec.mute?.disconnect(); } catch { /* already stopped */ }
    rec.stream.getTracks().forEach((track) => track.stop());
    const inRate = rec.ctx.sampleRate;
    await rec.ctx.close().catch(() => {});
    if (!rec.chunks.length) return "";
    const total = rec.chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const merged = new Float32Array(total);
    let offset = 0;
    rec.chunks.forEach((chunk) => {
        merged.set(chunk, offset);
        offset += chunk.length;
    });
    return pcm16ToBase64(floatToPcm16(downsample(merged, inRate, PCM_IN)));
};

const createPcmPlayer = () => {
    const ctx = new AudioContext({ sampleRate: PCM_OUT });
    let next = 0;
    return {
        push(base64) {
            const floats = decodePcm16(base64);
            if (!floats.length) return;
            const sourceRate = PCM_OUT;
            const play = ctx.sampleRate === sourceRate
                ? floats
                : downsample(floats, sourceRate, ctx.sampleRate);
            const buffer = ctx.createBuffer(1, play.length, ctx.sampleRate);
            buffer.getChannelData(0).set(play);
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            source.connect(ctx.destination);
            const start = Math.max(ctx.currentTime, next);
            source.start(start);
            next = start + buffer.duration;
        },
        whenIdle() {
            if (ctx.state === "closed") return Promise.resolve();
            return new Promise((resolve) => {
                const tick = () => {
                    if (ctx.state === "closed" || next - ctx.currentTime <= 0.02) {
                        resolve();
                        return;
                    }
                    const waitMs = Math.min(80, Math.max(16, (next - ctx.currentTime) * 1000));
                    setTimeout(tick, waitMs);
                };
                tick();
            });
        },
        close() {
            return ctx.close().catch(() => {});
        },
    };
};

const FILLED_TALK = /fill(?:ed)? the form|updated? the (?:form|draft)|draft is (?:in|ready)|here(?:'| i)s (?:the|your) (?:draft|post)|added (?:that|it|those) to the (?:form|draft)|check the form|take a look/i;

const BlogWriter = ({ title, body, onDraft, onDrafting, onClear, clearRef, disabled, draftId }) => {
    const saved = readComposeDraft(draftId);
    const [messages, setMessages] = useState(() => (
        saved?.writerMessages?.length
            ? saved.writerMessages
            : [{ role: "assistant", content: OPENER }]
    ));
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [listening, setListening] = useState(false);
    const [speakOn, setSpeakOn] = useState(true);
    const [applied, setApplied] = useState(() => Boolean(saved?.writerApplied));
    const [drafting, setDrafting] = useState(false);
    const [open, setOpen] = useState(() => saved?.writerOpen !== false);
    const listRef = useRef(null);
    const recRef = useRef(null);
    const listeningRef = useRef(false);
    const sendRef = useRef(null);
    const playerRef = useRef(null);
    const toggleMicRef = useRef(null);
    const busyRef = useRef(false);
    const openRef = useRef(true);
    const disabledRef = useRef(disabled);
    const sessionRef = useRef(0);
    busyRef.current = busy;
    openRef.current = open;
    disabledRef.current = disabled;

    useEffect(() => {
        const node = listRef.current;
        if (node) node.scrollTop = node.scrollHeight;
    }, [messages, busy]);

    useEffect(() => {
        warmBlogWriter();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            writeComposeDraft(draftId, {
                writerMessages: messages,
                writerApplied: applied,
                writerOpen: open,
            });
        }, 200);
        return () => {
            clearTimeout(timer);
            writeComposeDraft(draftId, {
                writerMessages: messages,
                writerApplied: applied,
                writerOpen: open,
            });
        };
    }, [draftId, messages, applied, open]);

    useEffect(() => () => {
        listeningRef.current = false;
        stopPcmCapture(recRef.current);
        recRef.current = null;
        playerRef.current?.close();
        window.speechSynthesis?.cancel();
    }, []);

    const draftingSinceRef = useRef(0);
    const draftingClearRef = useRef(null);
    const markDrafting = (on) => {
        if (on) {
            clearTimeout(draftingClearRef.current);
            if (!draftingSinceRef.current) draftingSinceRef.current = Date.now();
            setDrafting(true);
            onDrafting?.(true);
            draftingClearRef.current = setTimeout(() => {
                draftingSinceRef.current = 0;
                setDrafting(false);
                onDrafting?.(false);
            }, 10_000);
            return;
        }
        if (!draftingSinceRef.current) {
            setDrafting(false);
            onDrafting?.(false);
            return;
        }
        const wait = Math.max(0, 700 - (Date.now() - draftingSinceRef.current));
        clearTimeout(draftingClearRef.current);
        draftingClearRef.current = setTimeout(() => {
            draftingSinceRef.current = 0;
            setDrafting(false);
            onDrafting?.(false);
        }, wait);
    };
    useEffect(() => () => clearTimeout(draftingClearRef.current), []);
    const applyDraft = (draft) => {
        const nextTitle = String(draft?.title || "").trim();
        const nextBody = String(draft?.body || "").trim();
        if (!nextTitle && !nextBody) return;
        onDraft({ title: nextTitle, body: nextBody });
        setApplied(true);
        markDrafting(false);
    };

    const resetWriter = () => {
        sessionRef.current += 1;
        listeningRef.current = false;
        stopPcmCapture(recRef.current);
        recRef.current = null;
        window.speechSynthesis?.cancel();
        playerRef.current?.close();
        playerRef.current = null;
        clearTimeout(draftingClearRef.current);
        draftingSinceRef.current = 0;
        setListening(false);
        setBusy(false);
        setInput("");
        setError("");
        setApplied(false);
        setDrafting(false);
        onDrafting?.(false);
        setMessages([{ role: "assistant", content: OPENER }]);
        writeComposeDraft(draftId, {
            writerMessages: [{ role: "assistant", content: OPENER }],
            writerApplied: false,
            writerOpen: openRef.current,
        });
        onClear?.();
    };
    const clearAll = () => {
        if (disabledRef.current) return;
        const emptyChat = messages.length === 1
            && messages[0]?.role === "assistant"
            && messages[0]?.content === OPENER;
        const emptyForm = !String(title || "").trim() && !hasBlogBody(body);
        if (emptyChat && emptyForm && !applied) return;
        if (!window.confirm("Clear the chat and the form?")) return;
        resetWriter();
    };
    useEffect(() => {
        if (clearRef) clearRef.current = clearAll;
        return () => {
            if (clearRef) clearRef.current = null;
        };
    });

    const sendTurn = async ({ text, audioBase64 }) => {
        const spoken = String(text || "").trim();
        if ((!spoken && !audioBase64) || busy || disabled) return;
        let next = messages;
        let reply = "";
        let shownReply = false;
        let heardAudio = false;
        let unlocked = false;
        let waitingForSpeech = false;
        let streamAt = -1;
        let userAt = -1;
        if (spoken) {
            next = [...messages, { role: "user", content: spoken }];
            streamAt = next.length;
            setMessages([...next, { role: "assistant", content: WORKING }]);
            setInput("");
        }
        setError("");
        setBusy(true);
        const session = sessionRef.current;
        const live = () => session === sessionRef.current;
        const unlockMic = () => {
            if (unlocked) return;
            unlocked = true;
            setBusy(false);
        };
        const player = speakOn ? createPcmPlayer() : null;
        playerRef.current?.close();
        playerRef.current = player;
        let idleGen = 0;
        const armPlaybackUnlock = () => {
            if (!speakOn || unlocked || !player) return;
            waitingForSpeech = true;
            const gen = ++idleGen;
            player.whenIdle().then(() => {
                if (gen !== idleGen || unlocked) return;
                setTimeout(() => {
                    if (gen !== idleGen || unlocked) return;
                    unlockMic();
                }, 80);
            });
        };
        const takeTranscript = (text) => {
            const heard = String(text || "").trim();
            if (!heard || spoken) return;
            setMessages((prev) => {
                const rows = [...prev];
                if (userAt >= 0 && rows[userAt]?.role === "user") {
                    rows[userAt] = { role: "user", content: heard };
                    return rows;
                }
                userAt = rows.length;
                return [...rows, { role: "user", content: heard }];
            });
            if (next.at(-1)?.role === "user") {
                next = [...next.slice(0, -1), { role: "user", content: heard }];
            } else {
                next = [...next, { role: "user", content: heard }];
            }
        };
        const takeDelta = (piece) => {
            if (!piece || shownReply) return;
            reply += piece;
            setMessages((prev) => {
                const rows = [...prev];
                if (streamAt >= 0 && rows[streamAt]?.role === "assistant") {
                    if (rows[streamAt].content !== WORKING) {
                        rows[streamAt] = { role: "assistant", content: WORKING };
                    }
                    return rows;
                }
                streamAt = rows.length;
                return [...rows, { role: "assistant", content: WORKING }];
            });
        };
        const takeReply = (text) => {
            const said = String(text || reply || "").trim();
            if (!said) return;
            const first = !shownReply;
            shownReply = true;
            reply = said;
            setMessages((prev) => {
                const rows = [...prev];
                if (streamAt >= 0 && rows[streamAt]?.role === "assistant") {
                    rows[streamAt] = { role: "assistant", content: said };
                    return rows;
                }
                streamAt = rows.length;
                return [...rows, { role: "assistant", content: said }];
            });
            if (!first) return;
            if (!speakOn) {
                unlockMic();
                return;
            }
            if (heardAudio) {
                armPlaybackUnlock();
                return;
            }
            waitingForSpeech = true;
            speakReply(said, { onend: unlockMic });
        };
        let awaitingDraft = false;
        try {
            for await (const event of streamBlogWriter({
                messages: next,
                title,
                body,
                text: spoken,
                audioBase64,
            })) {
                if (!live()) return;
                if (event.type === "error") {
                    throw new Error(event.message || "Writer failed");
                }
                if (event.type === "transcript" && event.text) takeTranscript(event.text);
                if (event.type === "ai_delta" && event.text) takeDelta(event.text);
                if (event.type === "audio" && event.chunk) {
                    heardAudio = true;
                    player?.push(event.chunk);
                    armPlaybackUnlock();
                }
                if (event.type === "speech_end" && heardAudio) armPlaybackUnlock();
                if (event.type === "reply") {
                    takeReply(event.text || reply);
                    if (FILLED_TALK.test(event.text || reply)) {
                        awaitingDraft = true;
                        markDrafting(true);
                    }
                }
                if (event.type === "drafting") {
                    awaitingDraft = true;
                    markDrafting(true);
                }
                if (event.type === "draft") {
                    awaitingDraft = false;
                    applyDraft(event);
                }
                if (event.type === "draft_idle") {
                    awaitingDraft = false;
                    markDrafting(false);
                }
            }
            if (live()) takeReply(reply);
        } catch (err) {
            if (!live()) return;
            if (!spoken) {
                setError(err.message || "Could not talk to the writer");
                return;
            }
            try {
                const result = await talkBlogWriter({ messages: next, title, body });
                if (!live()) return;
                const fallback = result?.reply || "Got it — anything else I should include?";
                takeReply(fallback);
                applyDraft(result?.draft);
            } catch (fallbackErr) {
                if (!live()) return;
                setError(fallbackErr.message || err.message || "Could not talk to the writer");
            }
        } finally {
            if (!live()) return;
            if (!waitingForSpeech) unlockMic();
            if (awaitingDraft) markDrafting(false);
        }
    };
    sendRef.current = sendTurn;

    const toggleMic = async () => {
        if (listeningRef.current) {
            listeningRef.current = false;
            setListening(false);
            const audioBase64 = await stopPcmCapture(recRef.current);
            recRef.current = null;
            if (audioBase64) sendRef.current?.({ audioBase64 });
            return;
        }
        window.speechSynthesis?.cancel();
        playerRef.current?.close();
        setError("");
        try {
            recRef.current = await startPcmCapture();
            listeningRef.current = true;
            setListening(true);
        } catch {
            listeningRef.current = false;
            setListening(false);
            setError("Could not start the microphone.");
        }
    };
    toggleMicRef.current = toggleMic;

    useEffect(() => {
        const onKey = (event) => {
            if (event.repeat) return;
            if (event.key === "Escape" && listeningRef.current) {
                event.preventDefault();
                toggleMicRef.current?.();
                return;
            }
            if (!isMicShortcut(event)) return;
            if (disabledRef.current || (busyRef.current && !listeningRef.current)) return;
            event.preventDefault();
            if (!openRef.current) setOpen(true);
            toggleMicRef.current?.();
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, []);

    const canClear = applied
        || drafting
        || messages.length > 1
        || messages[0]?.content !== OPENER
        || Boolean(String(title || "").trim())
        || hasBlogBody(body);

    const chat = (
        <aside className="blog__writer-dock" aria-label="Writer assistant" data-blog-tour="writer">
            <div className="blog__writer-head">
                <div>
                    <p className="blog__writer-title">Writer assistant</p>
                    <p className="blog__writer-sub">Talk it through. I’ll fill the form for you to review. {MIC_SHORTCUT} toggles the mic.</p>
                </div>
                <div className="blog__writer-tools">
                    <button
                        type="button"
                        className={`blog__writer-speak${speakOn ? " is-on" : ""}`}
                        onClick={() => {
                            setSpeakOn((on) => {
                                if (on) {
                                    window.speechSynthesis?.cancel();
                                    playerRef.current?.close();
                                }
                                return !on;
                            });
                        }}
                        aria-pressed={speakOn}
                        aria-label={speakOn ? "Mute spoken replies" : "Speak replies"}
                        title={speakOn ? "Mute spoken replies" : "Speak replies"}
                    >
                        {speakOn ? <LuVolume2 size="1.1rem" /> : <LuVolumeX size="1.1rem" />}
                    </button>
                    <button
                        type="button"
                        className="blog__writer-speak"
                        onClick={() => {
                            window.speechSynthesis?.cancel();
                            playerRef.current?.close();
                            setOpen(false);
                        }}
                        aria-label="Minimize writer assistant"
                        title="Minimize"
                    >
                        <LuMinus size="1.1rem" />
                    </button>
                </div>
            </div>

            <div className="blog__writer-log" ref={listRef}>
                {messages.map((row, index) => {
                    const live = busy && row.role === "assistant" && index === messages.length - 1 && row.content !== WORKING;
                    return (
                        <p
                            key={`${row.role}-${index}`}
                            className={`blog__writer-bubble blog__writer-bubble--${row.role}${live ? " blog__writer-bubble--live" : ""}`}
                        >
                            {row.content}
                        </p>
                    );
                })}
                {listening ? <p className="blog__writer-status">Listening… {MIC_SHORTCUT} or Esc when you’re done.</p> : null}
                {busy && messages.at(-1)?.role !== "assistant" ? <p className="blog__writer-status">Talking…</p> : null}
                {drafting ? (
                    <p className="blog__writer-status blog__writer-status--draft" aria-live="polite">
                        <span className="blog__draft-spin" aria-hidden="true" />
                        Working on it…
                    </p>
                ) : null}
            </div>

            {applied && !drafting ? <p className="blog__writer-applied">Draft is in the form — edit anything before you publish.</p> : null}
            {error ? <p className="blog__error">{error}</p> : null}
            <button
                type="button"
                className="blog__btn blog__btn--danger blog__writer-clear"
                onClick={clearAll}
                disabled={disabled || !canClear}
            >
                Clear chat and form
            </button>

            <form
                className="blog__writer-form"
                onSubmit={(event) => {
                    event.preventDefault();
                    sendTurn({ text: input });
                }}
            >
                <input
                    type="text"
                    placeholder="Type or talk"
                    value={input}
                    disabled={busy || disabled}
                    onChange={(event) => setInput(event.target.value)}
                />
                <button
                    type="button"
                    className={`blog__writer-speak${listening ? " is-rec" : ""}`}
                    onClick={toggleMic}
                    disabled={busy || disabled}
                    aria-pressed={listening}
                    aria-label={listening ? `Stop talking (${MIC_SHORTCUT})` : `Talk to the writer assistant (${MIC_SHORTCUT})`}
                    title={listening ? `Stop talking (${MIC_SHORTCUT} or Esc)` : `Talk to the writer assistant (${MIC_SHORTCUT})`}
                >
                    {listening ? <AiOutlineAudioMuted size="1.2rem" /> : <AiOutlineAudio size="1.2rem" />}
                </button>
                <button type="submit" className="blog__btn blog__btn--ghost" disabled={busy || disabled || !input.trim()}>
                    Send
                </button>
            </form>
        </aside>
    );

    const launch = (
        <button
            type="button"
            className="blog__writer-launch"
            data-blog-tour="writer"
            onClick={() => setOpen(true)}
        >
            <LuMessageCircle size="1.15rem" />
            Writer assistant
        </button>
    );

    if (typeof document === "undefined") return null;
    return createPortal(
        <div className="blog blog--writer-layer">
            {open ? chat : launch}
        </div>,
        document.body,
    );
};

export default BlogWriter;
